import ToolTip from "components/design/toolTip";
import { Sync } from "components/icons/Sync";
import { INVOICE_SOURCE_SYNC_STATUS } from "constants/invoiceSourceSync";
import { REVENUE_DATA_SOURCE_OPTIONS } from "constants/revenueDataSource";
import { Invoice } from "types/Models/invoice";
import { InvoiceSourceSync } from "types/Models/invoiceSourceSync";
import { getSourceRemoval } from "utils/getSourceRemoval";

type InvoiceSyncIndicatorProps = {
  invoice: Invoice;
};

// Only invoices still waiting on a resync or a review get an icon; a voided one already shows Void
type IndicatorStatus =
  | typeof INVOICE_SOURCE_SYNC_STATUS.CHANGED
  | typeof INVOICE_SOURCE_SYNC_STATUS.NEEDS_REVIEW;

const ICON_COLOR: Record<IndicatorStatus, string> = {
  [INVOICE_SOURCE_SYNC_STATUS.CHANGED]: "t-text-orange",
  [INVOICE_SOURCE_SYNC_STATUS.NEEDS_REVIEW]: "t-text-red",
};

const isIndicatorStatus = (
  status: InvoiceSourceSync["status"]
): status is IndicatorStatus => status in ICON_COLOR;

const getIndicatorText = (
  sourceSync: InvoiceSourceSync,
  sourceName: string
) => {
  const removal = getSourceRemoval(sourceSync);
  const labels = sourceSync.changes.map((change) => change.label).join(", ");

  if (sourceSync.status === INVOICE_SOURCE_SYNC_STATUS.NEEDS_REVIEW) {
    const what = `${removal ?? "Changed"} in ${sourceName}`;
    return `${what}, needs review: ${sourceSync.blocker ?? ""}`;
  }

  return `Changed in ${sourceName}: ${labels}`;
};

// Quiet marker beside the source logo; the invoice banner carries the full diff
export const InvoiceSyncIndicator = ({
  invoice,
}: InvoiceSyncIndicatorProps) => {
  const sourceSync = invoice.source_sync;
  if (!sourceSync || !isIndicatorStatus(sourceSync.status)) {
    return null;
  }

  const text = getIndicatorText(
    sourceSync,
    REVENUE_DATA_SOURCE_OPTIONS[invoice.source]
  );

  return (
    <ToolTip text={text} side="top">
      <span
        role="img"
        aria-label={text}
        className={`t-flex ${ICON_COLOR[sourceSync.status]}`}
      >
        <Sync size="14" />
      </span>
    </ToolTip>
  );
};
